import React, { useEffect, useLayoutEffect } from 'react';

import { useWindowVirtualizer } from '@tanstack/react-virtual';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { mobile, useBreakpoint } from '~/techstyle-shared/react-components';

import {
  GAP_BETWEEN_GRID_COLUMNS_DESKTOP,
  GAP_BETWEEN_GRID_COLUMNS_MOBILE,
  GAP_BETWEEN_GRID_ROWS,
  GRID_CARD_TYPES,
  VIRTUAL_SCROLL_OVERSCAN,
  VIRTUAL_SCROLL_GRID_ID,
  OUTER_GRID_PADDING_MOBILE,
} from '../constants';
import useNumGridCols from '../hooks/useNumGridCols';
import {
  getEstimatedCardWidth,
  getEstimatedCellHeight,
} from '../utils/measurements';

import GridCardRendererV2 from './GridCardRendererV2';
import NoResultsFound from './NoResultsFound';
import ProductCardSkeleton from './ProductCard/ProductCardSkeleton';

const GridContainer = styled.div`
  width: 100%;

  ${mobile`
    padding: 0 ${OUTER_GRID_PADDING_MOBILE / 2}px;
  `}
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $numGridCols }) => $numGridCols}, 1fr);
  gap: ${GAP_BETWEEN_GRID_ROWS}px
    ${({ $isMobile }) =>
      $isMobile
        ? GAP_BETWEEN_GRID_COLUMNS_MOBILE
        : GAP_BETWEEN_GRID_COLUMNS_DESKTOP}px;
`;

const VirtualizerContainer = styled.div`
  height: ${({ $totalSize }) => $totalSize}px;
  position: relative;
`;

const VirtualRow = styled.div`
  position: absolute;
  top: 0;
  transform: translateY(${({ $rowStart }) => $rowStart}px);
  width: 100%;
`;

const RowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $numGridCols }) => $numGridCols}, 1fr);
  gap: ${GAP_BETWEEN_GRID_ROWS}px
    ${({ $isMobile }) =>
      $isMobile
        ? GAP_BETWEEN_GRID_COLUMNS_MOBILE
        : GAP_BETWEEN_GRID_COLUMNS_DESKTOP}px;
`;

// POC Reference: https://github.com/Fabletics/web-spa/pull/3883/files#diff-dfe3140fb2e0ace5e8430d5ba8ce5c99925d14e33102bc49717d1373b7d340f3

// IMPORTANT: make sure to not use expensive operations in this component. This component re-renders
// on scroll and resize per the virtualizer. There are optimizations with the virtualizer that does
// not re-paint the entire grid but it will re-render the items in view so avoid expensive operations
// in this component. This is especially true for any hooks, even if the hook has caching mechanisms.
// The hook will still be called on every render, so avoid hooks in this component
// If you need to use hooks, use them in the parent component and pass down the data.
const VirtualScrollGrid = ({
  fetchNextPage = () => {},
  handleOpenFilterDrawer,
  hasNextPage,
  isPrimaryProductsFetching,
  isPrimaryProductsLoading,
  virtualList = [],
}) => {
  const numGridCols = useNumGridCols();
  const { isMobile } = useBreakpoint();

  // Estimate card width from the current viewport to size skeletons and virtualizer estimates.
  const estimatedCardWidth = getEstimatedCardWidth({
    isMobile,
    numGridCols,
    viewportWidth: document?.documentElement?.clientWidth,
  });

  // Used for initial virtualizer estimates and skeleton sizing before real measurements.
  const estimatedCardHeight = getEstimatedCellHeight({
    isMobile,
    dynamicCardWidth: estimatedCardWidth,
  });

  const getEstimatedRowHeight = (index) => {
    const virtualRowItems = virtualList[index]; // each virtualRow will actually be rendered as a grid of items
    const assets = virtualRowItems.filter(
      (item) => item.type === GRID_CARD_TYPES.ASSET
    );

    if (!assets?.length) {
      return estimatedCardHeight; // rowSpan = 1 for non-asset items (product cards)
    }
    const minAssetRowIndex = Math.min(
      ...assets.map((item) => item.effectiveCustomVars?.row)
    );
    const maxAssetRowIndex = Math.max(
      ...assets.map((item) => item.effectiveCustomVars?.row + item.span.rows)
    );
    const rowSpan = maxAssetRowIndex - minAssetRowIndex;

    const rowHeight =
      estimatedCardHeight * rowSpan + GAP_BETWEEN_GRID_ROWS * (rowSpan - 1);

    return rowHeight;
  };

  const virtualizer = useWindowVirtualizer({
    count: virtualList?.length || 0,
    estimateSize: (index) => getEstimatedRowHeight(index),
    measureElement: (element) => element.getBoundingClientRect().height,
    gap: GAP_BETWEEN_GRID_ROWS,
    overscan: VIRTUAL_SCROLL_OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [numGridCols, isMobile, virtualizer]);

  useEffect(() => {
    if (virtualItems.length === 0) {
      return;
    }

    const lastItem = virtualItems.at(-1);
    if (!lastItem) {
      return;
    }

    const shouldFetchNextPage =
      lastItem.index >= virtualList?.length - 1 &&
      hasNextPage &&
      !isPrimaryProductsFetching;
    if (shouldFetchNextPage) {
      // make sure to not fetch if we are already fetching
      fetchNextPage();
    }
  }, [
    hasNextPage,
    isPrimaryProductsFetching,
    fetchNextPage,
    virtualItems,
    virtualList.length,
  ]);

  if (isPrimaryProductsLoading) {
    return (
      <GridContainer>
        <SkeletonGrid $numGridCols={numGridCols} $isMobile={isMobile}>
          {Array.from({ length: numGridCols * 3 }).map((_, index) => {
            return (
              <ProductCardSkeleton key={index} height={estimatedCardHeight} />
            );
          })}
        </SkeletonGrid>
      </GridContainer>
    );
  }

  if (!virtualList?.length) {
    return (
      <GridContainer>
        {handleOpenFilterDrawer ? (
          <NoResultsFound onClickButton={handleOpenFilterDrawer} />
        ) : (
          <NoResultsFound />
        )}
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <VirtualizerContainer
        $totalSize={virtualizer.getTotalSize()}
        id={VIRTUAL_SCROLL_GRID_ID}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const rowIndex = virtualRow.index;
          const rowItems = virtualList[rowIndex];

          return (
            <VirtualRow
              // Keep a stable React key to avoid unmounting row contents on resize.
              key={rowIndex}
              // measureElement is invoked by the virtualizer when the row mounts/updates to capture its actual height.
              ref={virtualizer.measureElement}
              data-index={rowIndex}
              $rowStart={virtualRow.start}
            >
              <RowGrid $numGridCols={numGridCols} $isMobile={isMobile}>
                {rowItems?.map((item, column) => {
                  return (
                    <GridCardRendererV2
                      key={item.id}
                      item={item}
                      row={rowIndex}
                      column={column}
                    />
                  );
                })}
              </RowGrid>
            </VirtualRow>
          );
        })}
      </VirtualizerContainer>
    </GridContainer>
  );
};

VirtualScrollGrid.propTypes = {
  fetchNextPage: PropTypes.func,
  handleOpenFilterDrawer: PropTypes.func,
  hasNextPage: PropTypes.bool,
  isPrimaryProductsFetching: PropTypes.bool,
  isPrimaryProductsLoading: PropTypes.bool,
  virtualList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        type: PropTypes.string,
        span: PropTypes.shape({
          rows: PropTypes.number,
          columns: PropTypes.number,
        }),
      })
    )
  ),
};

export default VirtualScrollGrid;
